import { datadogRum } from '@datadog/browser-rum'
import { useEffect } from 'react'
import { generateDataDogContext } from '../../utils/dataDog'
import {
  getOrderConfirmationConfigIssues,
  getUnknownOrderStatuses,
  isRequiredMisauthored,
  OrderConfirmationIssueReason,
} from '../../utils/orderConfirmation'
import useAppSelector from '../useAppSelector'

/**
 * How each of the three events reads to whoever opens the Datadog error.
 *
 * Worded separately rather than folded into one "misconfigured" sentence
 * because the fix differs by reason, and the clamp wording carries the load:
 * that value was **legitimate** and this client overrode it, so an author who
 * reads it as a rejection goes and changes a value that was never the problem.
 */
const ISSUE_WORDING: Record<OrderConfirmationIssueReason, string> = {
  rejected: 'is not a usable value, so the authored one does nothing and',
  clamped:
    'is a legitimate value that this client overrides by policy, so instead of it',
  emptyList:
    'is a list naming no usable status, which is read as unconfigured, so instead of it',
}

/**
 * Reports an `orderConfirmation` block that does not do what it says.
 *
 * Three effects: a status that is not an `OrderStatus` member, a `required`
 * that is not literally boolean, and — as of COM-871 — a numeric or list
 * parameter whose authored value is not the one the poll runs on.
 *
 * Extracted from `useOrderDetail` in COM-776 because the block acquired a
 * second consumer that quarter — `useOrderConfirmation`, the in-webview
 * create-and-confirm flow — and the guards were reachable from neither the
 * screen that now depends on them most nor any path a shopper takes before
 * Order Detail. A storefront that opted in and typo'd `successStatuses` got
 * precisely the failure described below, on the checkout CTA, with no record
 * anywhere unless a shopper independently went and opened an order.
 *
 * Called from both consumers rather than copied into the second: two `useEffect`
 * blocks reporting the same misconfiguration would drift, and the report text
 * is the only thing anyone debugging this ever sees.
 *
 * Idempotent per mount and cheap: both predicates are pure reads over a config
 * object that is usually absent, and they fire nothing in the common case.
 * Mounting both consumers at once therefore double-reports a genuinely broken
 * block, which is the right failure direction for a signal that is otherwise
 * silent.
 *
 * @param functionName the caller, so Datadog says which surface saw it.
 * @internal
 */
export const useOrderConfirmationConfigGuards = (functionName: string) => {
  const orderConfirmation = useAppSelector(
    (state) =>
      state.defaultSlice.sessionData?.configuration?.order?.confirmation,
  )

  /**
   * A configured status that is not an `OrderStatus` member matches no order
   * that will ever exist, so the flow polls to its deadline and reports a
   * timeout that never happened. Nothing server-side rejects it — the block is
   * free-form JSON — so this log is the only place it surfaces.
   *
   * Reported, not thrown: the screen still renders the order, and taking a
   * working page down over a config typo would be the larger outage. Same
   * shape as the `hidePrices` and `packages.sort` misconfiguration reports in
   * `fetchConfig`.
   */
  useEffect(() => {
    const unknownStatuses = getUnknownOrderStatuses(orderConfirmation)
    if (!unknownStatuses.length) return

    const context = generateDataDogContext({
      title: 'orderConfirmation statuses misconfigured',
      extraInfo: { function: functionName, unknownStatuses },
    })
    datadogRum.startView(context.viewName)
    datadogRum.addError(
      new Error(
        // Each member stringified rather than joined raw. A blank or `null`
        // member is a real authoring slip — `successStatuses: ["", ""]` used
        // to render as `…not OrderStatus members: , `, naming nothing and
        // reading as a bug in the report — and quoting is what makes an empty
        // string visible at all. Same reasoning as the `JSON.stringify` on
        // `required` below.
        `orderConfirmation names statuses that are not OrderStatus members: ${unknownStatuses
          .map((status) => JSON.stringify(status))
          .join(', ')}`,
      ),
      context,
    )
  }, [orderConfirmation, functionName])

  /**
   * The other way the block can be authored and do nothing.
   *
   * `required` is read as literally `true` and nothing else, which is right —
   * a quoted `"false"` must not opt a storefront in — but it makes the
   * likelier authoring slip invisible: write `required: "true"`, or leave
   * `required` out while authoring `successStatuses`, `pollingTime` and
   * `timeout`, and the whole block resolves to `isAsync: false`. The shopper
   * then sees a still-`PROCESSING` order rendered as final, and the storefront
   * that configured async confirmation has no way to tell it did not take.
   *
   * Its own effect rather than a second branch of the one above because the
   * two conditions are independent: a block can be wrong in both ways at once,
   * and each is a different fix.
   */
  useEffect(() => {
    if (!isRequiredMisauthored(orderConfirmation)) return

    const context = generateDataDogContext({
      title: 'orderConfirmation.required misconfigured',
      extraInfo: {
        function: functionName,
        // Stringified so `undefined`, `"true"` and `0` are distinguishable in
        // Datadog rather than all arriving as an absent field.
        required: JSON.stringify(orderConfirmation?.required ?? null),
      },
    })
    datadogRum.startView(context.viewName)
    datadogRum.addError(
      new Error(
        'orderConfirmation is authored but `required` is not literally true; the confirming and timeout states stay off',
      ),
      context,
    )
  }, [orderConfirmation, functionName])

  /**
   * The five numeric and list parameters, and the ways an authored one is not
   * the one in force (COM-871).
   *
   * Its own effect for the reason the second one is: this is a different fix
   * from either of the above, and a block can be wrong in all three ways at
   * once.
   *
   * **The thing to not get wrong here is the silence.** GraphQL renders an
   * omitted field as `null`, so the ordinary `{ required: true }` block sends
   * all five of these as `null` and every one is replaced by a default —
   * correctly, and this must say nothing about any of it. `isAuthored` in
   * `resolveWithIssues` is what draws that line; a `!== undefined` test in its
   * place would fire five times for the commonest correct block in the estate.
   *
   * One error per issue rather than one per block: the field and the reason
   * are what a monitor wants to group on, and folding five fields into one
   * message would make "which one" a parsing problem.
   *
   * **The view is started once, above the loop, and the two above are not a
   * precedent for doing it inside one.** `generateDataDogContext` takes
   * `viewName` from `window.location`, so every iteration produced the same
   * name — and all four upload workflows configure `trackViewsManually: true`,
   * under which `startView` *ends the view in flight* rather than being a
   * no-op for a name already current. A block with five issues therefore
   * restarted the real screen's view five times, each restart discarding the
   * RUM data collected since the last. One call, one view, five errors on it.
   *
   * True of *this* effect, not of the hook. All three effects call `startView`
   * on the same `viewName` and they are not mutually exclusive — a block that
   * is wrong about `required`, a status name and a parameter at once restarts
   * the view three times, by the same mechanism this removed from one of them.
   * The `startView`-per-error idiom is used at ~20 sites across cinnamon and
   * this is the worst of them fixed, not the pattern retired.
   * TODO(COM-882) collapse the three to one view per mount.
   */
  useEffect(() => {
    const issues = getOrderConfirmationConfigIssues(orderConfirmation)
    if (!issues.length) return

    // Destructured deliberately: `generateDataDogContext` derives `viewName`
    // from `window.location` alone, so a `title` or `extraInfo` passed here
    // would be built and dropped. The per-issue contexts below carry the
    // payload; this call exists only for the name.
    const { viewName } = generateDataDogContext({
      title: 'orderConfirmation parameters misconfigured',
    })
    // Load-bearing, not boilerplate: with manual views an error raised while
    // no view is active is dropped at assembly, so this is what makes the
    // errors below reportable at all.
    datadogRum.startView(viewName)

    issues.forEach(({ field, reason, authored, effective }) => {
      datadogRum.addError(
        new Error(
          `orderConfirmation.${field} ${JSON.stringify(authored)} ${
            ISSUE_WORDING[reason]
          } the poll runs on ${JSON.stringify(effective)}`,
        ),
        generateDataDogContext({
          title: `orderConfirmation.${field} ${reason}`,
          extraInfo: {
            function: functionName,
            field,
            reason,
            // Stringified for the reason `required` is: it is the difference
            // between an empty list and an absent one in the Datadog UI.
            authored: JSON.stringify(authored),
            effective: JSON.stringify(effective),
          },
        }),
      )
    })
  }, [orderConfirmation, functionName])
}

export default useOrderConfirmationConfigGuards
